import React, { useState } from "react";
import { Banknote, CalendarOff, ChevronDown, FileText, User, AlertCircle } from "lucide-react";
import { ouvrirPdf, messageErreur, urlServeur } from "./api";
import { Lien } from "./Lien";
import { Groupe, Ligne, Badge } from "./ui";
import { profsAvecPhoto } from "../lib/photosProfs";

/* Le prof du cours : sa petite photo ronde et son prénom, comme les familles l'ont vu
   dans d'autres applications. Sans photo connue, une pastille avec l'initiale. */
export const Profs = ({ texte, clair = false }) => (
  <div className="flex flex-wrap items-center gap-3 mt-2" data-testid="espace-profs">
    {profsAvecPhoto(texte).map((p) => (
      <span key={p.nom} className="flex items-center gap-1.5 text-xs font-medium">
        {p.image ? (
          <img src={p.image} alt="" loading="lazy" className="w-7 h-7 rounded-full object-cover"
            style={{ border: "2px solid #fff" }} />
        ) : (
          <span className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-semibold"
            style={{ background: "var(--primary)", color: "#fff" }}>{p.nom[0]}</span>
        )}
        <span style={clair ? { color: "#fff" } : undefined}>{p.nom}</span>
      </span>
    ))}
  </div>
);

const francs = (n) => `CHF ${Number(n || 0).toFixed(2)}`;

const COURT = { lundi: "LUN", mardi: "MAR", mercredi: "MER", jeudi: "JEU", vendredi: "VEN",
  samedi: "SAM", dimanche: "DIM" };
const jourCourt = (j) => COURT[String(j || "").trim().toLowerCase()] || String(j || "").slice(0, 3).toUpperCase();
const salle = (s) => (/^salle/i.test(s) ? s : `Salle ${s}`);
const JOURS_FR = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const dateCourte = (j) => {
  const d = new Date(`${j}T12:00:00`);
  return `${JOURS_FR[d.getDay()]} ${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/* La carte 10 cours numérique (école, 30.10.2026) : les séances faites, celles qui restent,
   jusqu'à quand elle vaut — les séances non utilisées sont perdues en fin de semestre.
   Toucher la carte montre la date de chaque cours compté, pour vérifier (Jen, 30.10.2026).
   Terminée : reprendre une carte (ce cours déjà coché) ou s'inscrire au semestre — sauf si
   la famille a déjà repris une carte (`suivie`), qui prend le relais. */
const JOURS_LONGS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
// Le mois abrégé : « vendredi 18 septembre » passait sur deux lignes à 390 px.
const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.",
  "oct.", "nov.", "déc."];
const dateLongue = (j) => {
  const d = new Date(`${j}T12:00:00`);
  return `${JOURS_LONGS[d.getDay()]} ${d.getDate()} ${MOIS_COURTS[d.getMonth()]}`;
};

const CarteDix = ({ carte, suivie = false }) => {
  const [ouvert, setOuvert] = useState(false);
  const fin = carte.fin ? carte.fin.split("-").reverse().join(".") : "";
  // Les séances d'avant l'application (notées par l'école) comptent aussi.
  const deja = carte.deja_faites || 0;
  const n = carte.faites ?? carte.utilisees.length;
  const terminee = carte.statut === "terminee";
  const expiree = carte.statut === "expiree";
  // Une carte reprise alors que la précédente a encore des séances : elle attend son tour.
  const aSuivre = !!carte.a_suivre && !terminee && !expiree;
  const hors = carte.hors_carte || [];
  const comptes = n + hors.length;
  const accent = terminee || expiree ? "#B45309" : "#0F766E";
  // Plus assez de cours au calendrier pour finir la carte : on le dit en clair.
  const auCalendrier = carte.seances_au_calendrier ?? carte.restantes;
  const perdues = !aSuivre && !terminee && !expiree ? Math.max(0, carte.restantes - auCalendrier) : 0;
  const s = (x) => (x > 1 ? "s" : "");
  return (
    <div className="mt-2.5 rounded-2xl p-3" style={{ background: terminee || expiree ? "#FFFBEB" : aSuivre ? "#F8FAFC" : "#F0FDFA" }}
      data-testid="espace-carte10">
      <button type="button" className="w-full text-left" onClick={() => setOuvert(!ouvert)} aria-expanded={ouvert}
        data-testid="espace-carte10-seances">
        <div className="flex items-center justify-between gap-2">
          {/* Deux cartes au même cours : « Carte n° 2 · 1/10 » (tient sur une ligne à 390 px). */}
          <div className="text-[12.5px] font-semibold whitespace-nowrap">
            {carte.nb_cartes > 1 ? `Carte n° ${carte.rang}` : "Carte 10 cours"} · {n}/{carte.nb}
          </div>
          <div className="text-[12px] font-medium whitespace-nowrap" style={{ color: aSuivre ? "#64748B" : accent }}>
            {terminee ? "Terminée" : expiree ? `Expirée le ${fin}` : aSuivre ? "À suivre"
              : `${carte.restantes} restante${s(carte.restantes)}`}
          </div>
        </div>
        <div className="flex gap-1 mt-2" aria-hidden="true">
          {Array.from({ length: carte.nb }).map((_, k) => (
            <span key={k} className="h-2 flex-1 rounded-full" style={{ background: k < n ? "#0D9488" : aSuivre ? "#E2E8F0" : "#CCFBF1" }} />
          ))}
        </div>
        {comptes > 0 && (
          <div className="flex items-center justify-between mt-2 text-[12px] font-semibold" style={{ color: accent }}>
            <span>{ouvert ? "Masquer les dates" : comptes === 1 ? "Voir la date du cours" : `Voir les dates des ${comptes} cours`}</span>
            <ChevronDown size={16} className="transition-transform" style={{ transform: ouvert ? "rotate(180deg)" : "none" }} />
          </div>
        )}
      </button>
      {ouvert && comptes > 0 && (
        <ol className="mt-2 rounded-xl bg-white divide-y text-[12.5px]" style={{ borderColor: "#E2E8F0" }} data-testid="espace-carte10-dates">
          {deja > 0 && (
            <li className="flex justify-between gap-3 px-3 py-2">
              <span className="font-medium whitespace-nowrap">{deja > 1 ? `Cours 1 à ${deja}` : "Cours 1"}</span>
              <span className="text-[var(--muted)] text-right">avant l'application (noté par l'école)</span>
            </li>
          )}
          {carte.utilisees.map((d, k) => (
            <li key={d} className="flex justify-between gap-3 px-3 py-2">
              <span className="font-medium whitespace-nowrap">Cours {deja + k + 1}</span><span className="whitespace-nowrap">{dateLongue(d)}</span>
            </li>
          ))}
          {hors.map((d) => (
            <li key={d} className="flex justify-between gap-3 px-3 py-2" style={{ color: "#B45309" }}>
              <span className="font-medium whitespace-nowrap">Hors carte</span><span className="whitespace-nowrap">{dateLongue(d)}</span>
            </li>
          ))}
        </ol>
      )}
      {ouvert && comptes > 0 && (
        <p className="text-[11.5px] mt-1.5 text-[var(--muted)]">Une date ne te semble pas juste ? Dis-le à l'école, elle corrige.</p>
      )}
      {aSuivre ? (
        <p className="text-[11.5px] mt-2 text-[var(--muted)]" data-testid="espace-carte10-a-suivre">
          Prend le relais quand la carte{carte.rang > 1 ? ` n° ${carte.rang - 1}` : ""} sera terminée{fin ? ` · valable jusqu'au ${fin}` : ""}.
        </p>
      ) : !terminee && !expiree && (perdues > 0 ? (
        <p className="text-[11.5px] mt-2" style={{ color: "#B45309" }} data-testid="espace-carte10-perdues">
          Valable jusqu'au {fin} : {auCalendrier ? `plus que ${auCalendrier} cours au calendrier d'ici là` : "plus aucun cours au calendrier d'ici là"},
          {" "}{perdues} séance{s(perdues)} {perdues > 1 ? "seront perdues" : "sera perdue"}.
        </p>
      ) : (
        <p className="text-[11.5px] mt-2 text-[var(--muted)]">
          Valable jusqu'au {fin} : encore {auCalendrier} cours au calendrier d'ici là. Les séances non utilisées sont perdues en fin de semestre.
        </p>
      ))}
      {hors.length > 0 && (
        <p className="text-[11.5px] mt-2" style={{ color: "#B45309" }}>
          {hors.length} séance{s(hors.length)} au-delà de la carte : à régler avec une nouvelle carte.
        </p>
      )}
      {(terminee || expiree) && !suivie && (
        // L'une sous l'autre : côte à côte, « Nouvelle carte 10 » débordait du bouton à 390 px.
        <div className="grid gap-2 mt-3">
          <Lien href={`/inscription/carte-10${carte.cours_id ? `?course=${encodeURIComponent(carte.cours_id)}` : ""}`}
            className="rounded-full text-center text-[13px] font-semibold py-2.5 text-white"
            style={{ background: "var(--primary)" }} data-testid="espace-carte10-nouvelle">Reprendre une carte 10 cours</Lien>
          <Lien href="/inscription" className="rounded-full text-center text-[13px] font-semibold py-2.5 border bg-white"
            style={{ borderColor: "var(--border)" }} data-testid="espace-carte10-semestre">S'inscrire au semestre</Lien>
        </div>
      )}
    </div>
  );
};

/* Deux cartes au même cours se suivent : le cours sur UNE ligne, ses cartes l'une sous
   l'autre (avant, le cours apparaissait deux fois : « 2 cours par semaine »). */
const regrouperCartes = (cours) => {
  const out = [];
  cours.forEach((c) => {
    const meme = c.carte10 && c.cours_id && out.find((x) => x.cours_id === c.cours_id);
    if (!meme) { out.push({ ...c, cartes10: c.carte10 ? [c.carte10] : [] }); return; }
    meme.cartes10 = [...meme.cartes10, c.carte10].sort((a, b) =>
      String(a.fin || "").localeCompare(String(b.fin || "")) || (a.rang || 0) - (b.rang || 0));
  });
  return out;
};

/** Un cours, comme une ligne d'agenda : le jour et l'heure en tête, puis quoi, où, avec qui. */
const LigneCours = ({ c, absent }) => (
  <div className="flex gap-3.5 px-4 py-3.5" data-testid={`espace-cours-${c.id}`}>
    <div className="w-[54px] shrink-0 self-start rounded-2xl text-center py-2"
      style={{ background: "var(--primary-tint)" }}>
      <div className="text-[10.5px] font-bold tracking-[0.08em]" style={{ color: "var(--primary)" }}>
        {jourCourt(c.jour)}
      </div>
      <div className="text-[15px] font-semibold tabular-nums leading-tight mt-0.5">{c.heure || "—"}</div>
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[15px] font-semibold leading-snug">{c.nom}</div>
      <div className="text-[12.5px] text-[var(--muted)] mt-0.5">
        {[c.heure && c.fin ? `${c.heure}–${c.fin}` : "", c.salle ? salle(c.salle) : ""]
          .filter(Boolean).join(" · ")}
      </div>
      {c.jusqu_au && (
        <span className="inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 mt-1.5 mr-1.5"
          style={{ background: "#F1F5F9", color: "#475569" }} data-testid="espace-cours-jusqu-au">
          Désinscrit·e · vient jusqu'au {c.jusqu_au.split("-").reverse().join(".")}
        </span>
      )}
      {absent && (
        <span className="inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 mt-1.5"
          style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="espace-cours-absent">
          Absent·e {absent}
        </span>
      )}
      {c.professeur && <Profs texte={c.professeur} />}
      {(c.cartes10 || []).map((k) => (
        <CarteDix key={k.inscription_id} carte={k} suivie={c.cartes10.some((x) => x !== k
          && (String(x.fin || "") > String(k.fin || "") || (x.fin === k.fin && (x.rang || 0) > (k.rang || 0))))} />
      ))}
    </div>
  </div>
);

/* Mes cours, pour un élève : sa carte d'identité, ses cours de la semaine, puis ce qu'on
   peut faire — en liste, comme les réglages d'un iPhone. Tout tient au pouce. */
export const CarteEnfant = ({ enfant, onAttestation, children, absence, annoncees, reservations = null, sansEntete = false, rond = null }) => {
  const [fiche, setFiche] = useState(false);
  const [absenceOuverte, setAbsenceOuverte] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const sansAvs = enfant.cours.length > 0 && !String(enfant.avs || "").trim();
  const lignes = regrouperCartes(enfant.cours);
  const prochaineAbsence = (cours) => {
    // Par l'identifiant du cours : deux cours du même nom (Hip-Hop lundi et jeudi)
    // étaient tous deux marqués absents.
    const a = (enfant.absences || []).find((x) =>
      (x.cours_id ? x.cours_id === cours.cours_id : x.cours_nom === cours.nom)
      || x.cours_nom === "Tous ses cours");
    return a ? (a.du === a.au ? `le ${dateCourte(a.du)}` : `du ${dateCourte(a.du)} au ${dateCourte(a.au)}`) : "";
  };

  return (
    <section className="space-y-3" data-testid={`espace-enfant-${enfant.id}`}>
      {/* Avec plusieurs enfants, les tuiles du haut disent déjà qui on regarde. */}
      {!sansEntete && <div className="flex items-center gap-3 px-1">
        {/* Son rond : la photo ou l'avatar choisi par la famille, sinon l'initiale. */}
        {rond || (
          <div className="w-12 h-12 rounded-full grid place-items-center shrink-0 text-white text-lg font-semibold"
            style={{ background: "linear-gradient(140deg,#00C6CE,#0F1B2D)" }}>
            {(enfant.prenom || "?")[0]}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-display text-[22px] leading-tight truncate">{enfant.prenom} {enfant.nom}</div>
          <div className="text-[13px] text-[var(--muted)]">
            {lignes.length
              ? `${lignes.length} cours par semaine`
              : "Pas de cours en ce moment"}
          </div>
        </div>
      </div>}

      {enfant.cours.length > 0 && (
        <Groupe testId={`espace-cours-liste-${enfant.id}`}>
          {lignes.map((c) => <LigneCours key={c.id} c={c} absent={prochaineAbsence(c)} />)}
        </Groupe>
      )}

      {/* Ses réservations à venir (cours privé, stage, workshop…), juste sous ses cours et
          sous la même forme (Jen, 01.11.2026). */}
      {reservations}

      {annoncees}
      {confirmation && (
        <p className="text-[13px] px-1" style={{ color: "#15803D" }} data-testid="espace-absence-confirmee">
          {confirmation}
        </p>
      )}

      <Groupe>
        {enfant.cours.length > 0 && (
          <Ligne Icone={CalendarOff} couleur="#F59E0B" titre="Annoncer une absence"
            sousTitre="L'école et le prof sont prévenus"
            onClick={() => setAbsenceOuverte(!absenceOuverte)} ouvert={absenceOuverte}
            testId={`espace-absence-ouvrir-${enfant.id}`} />
        )}
        {absenceOuverte && absence && (
          <div className="px-4 py-4">
            {absence((message) => { setAbsenceOuverte(false); setConfirmation(message || ""); })}
          </div>
        )}
        {enfant.attestation_lien && (
          <Ligne Icone={FileText} couleur="#16A34A" titre="Attestation de paiement"
            sousTitre="Pour ton assurance maladie (PDF)" onClick={() => onAttestation(enfant)}
            testId={`espace-attestation-${enfant.id}`} />
        )}
        <Ligne Icone={User} couleur="#64748B" titre={`Fiche de ${enfant.prenom}`}
          sousTitre="Date de naissance, n° AVS, photos"
          badge={sansAvs ? <Badge>AVS manquant</Badge> : null}
          onClick={() => setFiche(!fiche)} ouvert={fiche}
          testId={`espace-ouvrir-fiche-${enfant.id}`} />
        {fiche && <div className="px-4 pb-4">{children}</div>}
      </Groupe>
    </section>
  );
};

/* Les factures de la famille : une ligne par facture, le montant et l'état à droite. On
   touche la ligne, le PDF s'ouvre (avec le QR-code pour payer). */
export const Factures = ({ factures }) => {
  const [erreur, setErreur] = useState("");
  const secours = async (f) => {
    setErreur("");
    try {
      await ouvrirPdf(`/facture/${encodeURIComponent(f.numero)}/pdf`);
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };

  if (!factures.length) {
    return (
      <Groupe>
        <p className="text-sm text-[var(--muted)] px-4 py-4">Aucune facture pour l'instant.</p>
      </Groupe>
    );
  }

  return (
    <div className="space-y-3" data-testid="espace-factures">
      {erreur && (
        <div className="text-sm rounded-2xl px-4 py-3" data-testid="espace-facture-erreur"
          style={{ background: "#FEF2F2", color: "#B91C1C" }}>{erreur}</div>
      )}
      <Groupe>
        {factures.map((f) => {
          if (f.especes) {
            // Espèces : pas de facture, pas de statut — l'école confirme à la main.
            return (
              <div key={`especes-${f.semestre}-${f.eleves}`} className="w-full flex items-center gap-3 px-4 py-3.5" data-testid="espace-facture-especes">
                <span className="grid place-items-center shrink-0 w-10 h-10 rounded-xl" style={{ background: "#F1F5F9", color: "#475569" }}>
                  <Banknote size={19} />
                </span>
                <span className="flex-1 min-w-0 text-left">
                  <span className="block text-[15px] font-medium truncate">{f.semestre || "Cours"}</span>
                  <span className="block text-[12.5px] text-[var(--muted)] truncate">{f.eleves}</span>
                  <span className="block text-[12px] text-[var(--muted)] mt-0.5">Paiement en espèces, à l'école</span>
                </span>
                {f.total > 0 && <span className="text-[15px] font-semibold tabular-nums shrink-0">{francs(f.total)}</span>}
              </div>
            );
          }
          const du = f.solde > 0.5;
          const contenu = (
            <>
              <span className="grid place-items-center shrink-0 w-10 h-10 rounded-xl"
                style={{ background: du ? "#FEF3C7" : "#DCFCE7", color: du ? "#B45309" : "#15803D" }}>
                <FileText size={19} />
              </span>
              <span className="flex-1 min-w-0 text-left">
                <span className="block text-[15px] font-medium truncate">{f.semestre || f.numero || "Facture"}</span>
                <span className="block text-[12.5px] text-[var(--muted)] truncate">
                  {[f.numero, f.eleves].filter(Boolean).join(" · ")}
                </span>
                {f.pdf
                  ? <span className="block text-[12.5px] font-medium mt-0.5" style={{ color: "var(--primary)" }}>Ouvrir le PDF</span>
                  : <span className="block text-[12px] text-[var(--muted)] mt-0.5">
                    {f.gratuite ? "Rien à payer : pas de facture émise pour CHF 0.–" : "PDF bientôt disponible"}
                  </span>}
              </span>
              <span className="text-right shrink-0">
                <span className="block text-[15px] font-semibold tabular-nums">{francs(f.total)}</span>
                <span className="inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 mt-1"
                  style={du ? { background: "#FEF3C7", color: "#92400E" } : { background: "#DCFCE7", color: "#166534" }}>
                  {du ? `${francs(f.solde)} à payer` : f.gratuite ? "Offert" : "Payée"}
                </span>
              </span>
            </>
          );
          const classes = "w-full flex items-center gap-3 px-4 py-3.5 active:bg-[#F1F5F9]";
          if (f.pdf_lien) {
            return (
              <a key={f.numero} href={urlServeur(f.pdf_lien)} target="_blank" rel="noopener noreferrer"
                className={classes} data-testid={`espace-facture-pdf-${f.numero}`}>{contenu}</a>
            );
          }
          if (f.pdf) {
            return (
              <button key={f.numero} onClick={() => secours(f)} className={classes}
                data-testid={`espace-facture-pdf-${f.numero}`}>{contenu}</button>
            );
          }
          return <div key={f.numero} className={classes} data-testid={`espace-facture-${f.numero}`}>{contenu}</div>;
        })}
      </Groupe>
    </div>
  );
};

/* Le rappel de ce qui reste dû. Le paiement se fait au bulletin QR de la facture :
   aucun paiement n'est encaissé dans l'app (règle des stores, et aucun frais en plus). */
export const ASolder = ({ montant, onVoir }) => {
  if (!montant || montant <= 0.5) return null;
  return (
    <div className="card-flat flex items-start gap-3" data-testid="espace-solde-alerte"
      style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}>
      <AlertCircle size={18} className="shrink-0 mt-0.5" style={{ color: "#B45309" }} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">
          <span className="font-mono">{francs(montant)}</span> encore à payer
        </div>
        <p className="text-xs text-[var(--muted)] mt-1">
          Le bulletin avec le QR-code se trouve sur ta facture : ouvre-la, scanne le code
          dans ton application bancaire et c'est réglé.
        </p>
        {onVoir && (
          <button onClick={onVoir} className="btn-ghost mt-3 text-sm"
            data-testid="espace-solde-voir-factures">
            Voir mes factures
          </button>
        )}
      </div>
    </div>
  );
};
